/**
 * IDP-983: Microapp Staging Environments
 * Environments widget for microapp admin page.
 *
 * Shows staging + production rows when microapp/stagingEnabled annotation is true.
 * Shows legacy production-only UI when annotation is false/missing (backward compat).
 *
 * Actions:
 *  - Deploy to Staging: navigates to the microapp-hosting template with staging
 *    enabled (same pattern as redeploy) — deploys build via the scaffolder.
 *  - Promote: calls /microapps-api/promote (no rebuild) and refreshes status.
 *  - Teardown: calls /microapps-api/teardown to remove the staging environment.
 *
 * Per-environment commit hashes come from /microapps-api/environment-status.
 */
/* eslint-disable no-restricted-syntax */
import { default as React } from 'react';

/* eslint-disable no-restricted-syntax */
import { useCallback, useEffect, useState } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Button,
  CircularProgress,
  Typography,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Checkbox,
  FormControlLabel,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import {
  useApi,
  identityApiRef,
  fetchApiRef,
} from '@backstage/core-plugin-api';
import { useEntity } from '@backstage/plugin-catalog-react';
import { useNavigate } from 'react-router-dom';
import { buildMicroappRedeployFormData } from './microappRedeployForm';
import {
  useCurrentUserEmail,
  isUserAuthorizedForEntity,
  useSkyStageAdminEmails,
  clearStagingTeardown,
  notifyPromoteComplete,
} from './hooks';
import { STAGING_ENABLED } from './stagingFeatureFlag';

// GCP Workload Identity Pool configuration for Quick Projects
// Dev: plateng-dev-app-wsky (project 880599546964)
// Prod: plateng-prod-app-wsky (project 791409896686)
const WORKLOAD_IDENTITY_POOLS = {
  dev: 'principal://iam.googleapis.com/projects/880599546964/locations/global/workloadIdentityPools/plateng-dev-app-wsky.svc.id.goog',
  prod: 'principal://iam.googleapis.com/projects/791409896686/locations/global/workloadIdentityPools/plateng-prod-app-wsky.svc.id.goog',
};

interface EnvState {
  deployed: boolean;
  commit: string | null;
  health: string | null;
  sync: string | null;
  url: string | null;
  /** IDP-983: true when environment is scaled to zero (e.g. after promote) */
  scaledToZero?: boolean;
}

interface EnvironmentStatus {
  production: EnvState;
  staging: EnvState;
}

export const MicroappEnvironmentsCard = () => {
  const { entity } = useEntity();
  const identityApi = useApi(identityApiRef);
  const fetchApi = useApi(fetchApiRef);
  const navigate = useNavigate();
  const appName = entity.metadata.name;

  // IDP-983: the Environments widget (deploy/promote/teardown + staging URLs) is
  // admin/owner-only. Non-owners don't see it at all.
  const currentUserEmail = useCurrentUserEmail();
  const adminEmails = useSkyStageAdminEmails();
  const isAuthorized = isUserAuthorizedForEntity(
    entity,
    currentUserEmail,
    adminEmails,
  );

  const [loading, setLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [status, setStatus] = useState<EnvironmentStatus | null>(null);
  const [promoteDialogOpen, setPromoteDialogOpen] = useState(false);
  const [wifCopied, setWifCopied] = useState(false);
  const [keepStaging, setKeepStaging] = useState(false);

  // Detect environment from the browser's current hostname for WIF pool
  const hostname =
    typeof window !== 'undefined' ? window.location.hostname : '';
  const isDevEnvironment =
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname.includes('.dev.wellsky.cloud');
  const workloadIdentityPool = isDevEnvironment
    ? WORKLOAD_IDENTITY_POOLS.dev
    : WORKLOAD_IDENTITY_POOLS.prod;

  // Compute namespaces and WIF principals for both environments
  const productionNamespace = `qp-${appName}-ns`;
  const k8sServiceAccount = `qp-${appName}-sa`;
  const productionWifPrincipal = `${workloadIdentityPool}/subject/ns/${productionNamespace}/sa/${k8sServiceAccount}`;

  // IDP-983 FIX: Show the Environments card if (a) the annotation says staging
  // is enabled, OR (b) either environment is actually deployed (handles legacy
  // apps that deployed before the annotation existed, and apps where production
  // deployed first then staging was added).
  const stagingEnabledAnnotation =
    entity.metadata.annotations?.['microapp/stagingEnabled'] === 'true';

  const fetchStatus = useCallback(async () => {
    setStatusLoading(true);
    try {
      const { token } = await identityApi.getCredentials();
      const response = await fetchApi.fetch(
        `/api/microapps-api/environment-status/${appName}`,
        {
          headers: {
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        },
      );
      if (response.ok) {
        const data = await response.json();
        setStatus({
          production: data.production,
          staging: data.staging,
        });
      }
    } catch {
      // Non-fatal: leave status null; rows fall back to "Not deployed".
    } finally {
      setStatusLoading(false);
    }
  }, [appName, fetchApi, identityApi]);

  useEffect(() => {
    void fetchStatus();
  }, [fetchStatus]);

  // Deploy to Staging: navigate to the template with staging enabled, reusing
  // the same annotation → formData pre-fill as redeploy.
  const handleDeployToStaging = () => {
    // IDP-983: clear any in-progress teardown flag so a fresh staging deploy is
    // never hidden by a stale optimistic-teardown override.
    clearStagingTeardown(appName);
    const formData = {
      ...(buildMicroappRedeployFormData(entity) ?? {}),
      actionType: 'redeploy',
      enableStagingEnvironment: true,
    };
    sessionStorage.setItem(`redeployed-${appName}`, '1');
    // Store timestamp to help identify this is a staging deploy
    sessionStorage.setItem(`staging-deploy-initiated`, Date.now().toString());
    navigate(
      `/create/templates/default/microapp-hosting?formData=${encodeURIComponent(
        JSON.stringify(formData),
      )}`,
    );
  };

  const handlePromoteClick = () => {
    setPromoteDialogOpen(true);
  };

  const handlePromoteCancel = () => {
    setPromoteDialogOpen(false);
    setWifCopied(false);
    setKeepStaging(false);
  };

  const handleCopyWif = async () => {
    try {
      await navigator.clipboard.writeText(productionWifPrincipal);
      setWifCopied(true);
      setTimeout(() => setWifCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = productionWifPrincipal;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setWifCopied(true);
      setTimeout(() => setWifCopied(false), 2000);
    }
  };

  const handlePromoteConfirm = async () => {
    setPromoteDialogOpen(false);
    setWifCopied(false);
    const shouldKeepStaging = keepStaging;
    setKeepStaging(false);
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const { token } = await identityApi.getCredentials();
      const response = await fetchApi.fetch('/api/microapps-api/promote', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({ appName, keepStaging: shouldKeepStaging }),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to promote to production');
      }

      // IDP-983: The backend commits the production manifests and triggers ArgoCD
      // but does NOT block on the sync (a synchronous wait timed out at the
      // gateway with a 502). Poll the environment status here so the user still
      // sees "wait until production is live" without the request timing out.
      setSuccess('Promoted. Waiting for production to become live…');
      const deadline = Date.now() + 180_000; // up to ~3 minutes
      let live = false;
      while (Date.now() < deadline) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise(resolve => setTimeout(resolve, 5000));
        try {
          // eslint-disable-next-line no-await-in-loop
          const { token: pollToken } = await identityApi.getCredentials();
          // eslint-disable-next-line no-await-in-loop
          const statusResp = await fetchApi.fetch(
            `/api/microapps-api/environment-status/${appName}`,
            {
              headers: {
                ...(pollToken && { Authorization: `Bearer ${pollToken}` }),
              },
            },
          );
          if (statusResp.ok) {
            // eslint-disable-next-line no-await-in-loop
            const data = await statusResp.json();
            // Update UI in real-time during polling so user sees progress
            setStatus({
              production: data.production,
              staging: data.staging,
            });
            if (
              data.production?.sync === 'Synced' &&
              data.production?.health === 'Healthy'
            ) {
              live = true;
              break;
            }
          }
        } catch {
          // Transient poll failure — keep waiting until the deadline.
        }
      }

      // IDP-983: After promote, staging is scaled to zero (unless keepStaging).
      // Poll a few more times to catch the staging scaledToZero flag so the UI
      // hides the staging row immediately without requiring a manual page refresh.
      if (live && !shouldKeepStaging) {
        setSuccess('Production is live. Waiting for staging to scale down…');
        const stagingDeadline = Date.now() + 30_000; // up to 30 seconds
        while (Date.now() < stagingDeadline) {
          // eslint-disable-next-line no-await-in-loop
          await new Promise(resolve => setTimeout(resolve, 3000));
          try {
            // eslint-disable-next-line no-await-in-loop
            const { token: stagingPollToken } =
              await identityApi.getCredentials();
            // eslint-disable-next-line no-await-in-loop
            const stagingStatusResp = await fetchApi.fetch(
              `/api/microapps-api/environment-status/${appName}`,
              {
                headers: {
                  ...(stagingPollToken && {
                    Authorization: `Bearer ${stagingPollToken}`,
                  }),
                },
              },
            );
            if (stagingStatusResp.ok) {
              // eslint-disable-next-line no-await-in-loop
              const stagingData = await stagingStatusResp.json();
              setStatus({
                production: stagingData.production,
                staging: stagingData.staging,
              });
              // Staging scaled to zero when health is Suspended or scaledToZero flag is true
              if (
                stagingData.staging?.scaledToZero ||
                stagingData.staging?.health === 'Suspended'
              ) {
                break;
              }
            }
          } catch {
            // Transient poll failure — keep waiting.
          }
        }
      }

      // Final refresh in case the last poll missed something
      await fetchStatus();
      // Notify other consumers (e.g. ArgoCD card) to refresh their status
      notifyPromoteComplete(appName);
      if (live) {
        setSuccess(
          shouldKeepStaging
            ? 'Production is live. Staging kept running. ✓'
            : 'Production is live. ✓',
        );
      } else {
        setSuccess(
          'Production promoted successfully! Status will update as it converges.',
        );
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const stagingDeployed = status?.staging?.deployed ?? false;
  const stagingCommit = status?.staging?.commit ?? null;
  const productionCommit = status?.production?.commit ?? null;
  const stagingUrl = status?.staging?.url ?? null;
  const productionDeployed = status?.production?.deployed ?? false;
  const productionUrl = status?.production?.url ?? null;
  // IDP-983: Hide staging row when scaled to zero (after promote to production)
  const stagingScaledToZero = status?.staging?.scaledToZero ?? false;
  const showStagingRow = stagingDeployed && !stagingScaledToZero;

  // IDP-983 (iii): render an environment's URL as a clickable hostname link so
  // owners can see and open the exact URL per environment. Shows "Not deployed"
  // when the environment has no live deployment.
  const renderEnvUrl = (deployed: boolean, url: string | null) => {
    if (statusLoading) {
      return (
        <Typography variant="body2" color="textSecondary">
          …
        </Typography>
      );
    }
    if (!deployed || !url) {
      return (
        <Typography variant="body2" color="textSecondary">
          Not deployed
        </Typography>
      );
    }
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        style={{ fontSize: '0.8125rem' }}
      >
        {url.replace(/^https?:\/\//, '')} ↗
      </a>
    );
  };
  // IDP-983 (iv): only allow Promote when staging has a NEWER image version than
  // production. `stagingCommit`/`productionCommit` now carry the deployed image
  // build tag (see backend /environment-status), so this correctly compares the
  // actual deployed code per environment. If production is not deployed yet, the
  // first promote is allowed. A null staging version (staging deployed but ArgoCD
  // hasn't reported an image yet) is treated as "unknown / not promotable" so we
  // never promote an indeterminate staging state.
  const stagingHasNewCommit =
    !!stagingCommit &&
    (!productionCommit || stagingCommit !== productionCommit);
  const promoteDisabled = loading || !stagingDeployed || !stagingHasNewCommit;

  // IDP-983: admin/owner-only widget — hidden entirely for everyone else.
  if (!isAuthorized) {
    return null;
  }

  // IDP-983 FIX: Show the Environments card if staging is enabled by annotation,
  // OR if any environment is actually deployed (handles apps that were deployed
  // before the annotation existed, or production-first deploys where staging was
  // added later). This ensures the card appears regardless of deploy order.
  const anyEnvDeployed = productionDeployed || stagingDeployed;
  const showEnvironmentsCard = stagingEnabledAnnotation || anyEnvDeployed;

  if (!showEnvironmentsCard) {
    return null;
  }

  // Staging-enabled UI
  return (
    <Card>
      <CardHeader title="Environments" />
      <CardContent>
        {error && (
          <Box mb={2}>
            <Alert severity="error">{error}</Alert>
          </Box>
        )}
        {success && (
          <Box mb={2}>
            <Alert severity="success">{success}</Alert>
          </Box>
        )}

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Environment</TableCell>
              <TableCell>URL</TableCell>
              {/* IDP-983: shows the deployed image build version (per env),
                  not the shared GitOps repo commit. */}
              <TableCell>Version</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {/* IDP-983: Hide staging row when scaled to zero (after promote) */}
            {showStagingRow && (
              <TableRow>
                <TableCell>
                  <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                    Staging
                  </Typography>
                </TableCell>
                <TableCell>
                  {renderEnvUrl(stagingDeployed, stagingUrl)}
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="textSecondary">
                    {statusLoading ? '…' : stagingCommit || 'Not deployed'}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Button
                    size="small"
                    variant="outlined"
                    color="primary"
                    disabled={loading || !STAGING_ENABLED}
                    onClick={handleDeployToStaging}
                    title={
                      !STAGING_ENABLED
                        ? 'Staging is temporarily disabled'
                        : undefined
                    }
                  >
                    Deploy to Staging
                  </Button>
                </TableCell>
              </TableRow>
            )}
            <TableRow>
              <TableCell>
                <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                  Production
                </Typography>
              </TableCell>
              <TableCell>
                {renderEnvUrl(productionDeployed, productionUrl)}
              </TableCell>
              <TableCell>
                <Typography variant="body2" color="textSecondary">
                  {statusLoading ? '…' : productionCommit || 'Not deployed'}
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  disabled={promoteDisabled}
                  title={
                    stagingDeployed && !stagingHasNewCommit
                      ? 'Staging matches production — nothing new to promote'
                      : undefined
                  }
                  onClick={handlePromoteClick}
                >
                  {loading ? <CircularProgress size={20} /> : 'Promote'}
                </Button>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>

        <Box mt={2}>
          <Typography variant="caption" color="textSecondary">
            Promote deploys the exact staging image to production without
            rebuilding. After promote, staging is scaled to zero and hidden. Use
            "Deploy to Staging" to test new changes before promoting again.
          </Typography>
        </Box>
      </CardContent>

      {/* Promote Confirmation Dialog with Production WIF Principal */}
      <Dialog
        open={promoteDialogOpen}
        onClose={handlePromoteCancel}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Promote to Production</DialogTitle>
        <DialogContent>
          <Box mb={2}>
            <Alert severity="warning">
              <Typography variant="body2" style={{ fontWeight: 500 }}>
                Before promoting, ensure the production WIF principal has the
                required IAM permissions.
              </Typography>
            </Alert>
          </Box>

          <Typography
            variant="subtitle2"
            style={{ fontWeight: 700, marginBottom: 8 }}
          >
            Production WIF Principal
          </Typography>
          <Box
            display="flex"
            alignItems="flex-start"
            bgcolor="grey.100"
            borderRadius={4}
            p={1.5}
            mb={2}
          >
            <Typography
              variant="body2"
              style={{
                fontFamily: 'monospace',
                fontSize: '0.75rem',
                wordBreak: 'break-all',
                flex: 1,
              }}
            >
              {productionWifPrincipal}
            </Typography>
            <IconButton
              size="small"
              onClick={handleCopyWif}
              title={wifCopied ? 'Copied!' : 'Copy principal'}
              style={{ marginLeft: 8 }}
            >
              <FileCopyIcon fontSize="small" />
            </IconButton>
          </Box>

          <Typography
            variant="body2"
            color="textSecondary"
            style={{ marginBottom: 16 }}
          >
            Grant this <strong>production WIF principal</strong> the required
            IAM roles to access GCP resources (Secret Manager, Cloud Storage,
            BigQuery, etc.). If your app uses GCP secrets, the production
            environment will fail to start without proper permissions.
          </Typography>

          <Typography variant="body2" style={{ marginBottom: 8 }}>
            This action will:
          </Typography>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            <li>
              <Typography variant="body2">
                Deploy the exact staging image to production (no rebuild)
              </Typography>
            </li>
            {!keepStaging && (
              <li>
                <Typography variant="body2">
                  Scale staging to zero after production is live
                </Typography>
              </li>
            )}
          </ul>

          <Box mt={2}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={keepStaging}
                  onChange={e => setKeepStaging(e.target.checked)}
                  color="primary"
                />
              }
              label="Keep staging running after promotion"
            />
            <Box ml={4} display="block">
              <Typography variant="caption" color="textSecondary">
                When unchecked, staging will be scaled to zero replicas to save
                resources.
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handlePromoteCancel} color="default">
            Cancel
          </Button>
          <Button
            onClick={handlePromoteConfirm}
            color="primary"
            variant="contained"
          >
            Confirm Promote
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};
